import type { ChangeEvent } from 'react';
import { useCallback, useState } from 'react';
import type { OnMove } from '@xyflow/react';
import {
  ZOOM_STEP_DURATION,
  updateZoomFromSlider,
} from './treemap-interaction-helpers';
import type { SetZoomLevel, TreemapReactFlowInstance } from './treemap-interaction-types';

interface TreemapViewportControls {
  handleReactFlowInit: (instance: TreemapReactFlowInstance) => void;
  handleReactFlowMove: OnMove;
  handleToggleMinimap: () => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleZoomSliderChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isMinimapVisible: boolean;
  reactFlowInstance: TreemapReactFlowInstance | null;
  setZoomLevel: SetZoomLevel;
  zoomLevel: number;
}

export function useTreemapViewportControls(): TreemapViewportControls {
  const [isMinimapVisible, setIsMinimapVisible] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<TreemapReactFlowInstance | null>(null);

  const handleZoomIn = useCallback((): void => {
    reactFlowInstance?.zoomIn({ duration: ZOOM_STEP_DURATION });
  }, [reactFlowInstance]);

  const handleZoomOut = useCallback((): void => {
    reactFlowInstance?.zoomOut({ duration: ZOOM_STEP_DURATION });
  }, [reactFlowInstance]);

  const handleZoomSliderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      updateZoomFromSlider({ event, reactFlowInstance, setZoomLevel });
    },
    [reactFlowInstance],
  );

  const handleToggleMinimap = useCallback((): void => {
    setIsMinimapVisible((previous) => !previous);
  }, []);

  const handleReactFlowInit = useCallback(
    (instance: TreemapReactFlowInstance): void => {
      setReactFlowInstance(instance);
      setZoomLevel(instance.getZoom());
    },
    [],
  );

  const handleReactFlowMove = useCallback<OnMove>((_event, viewport): void => {
    setZoomLevel(viewport.zoom);
  }, []);

  return {
    handleReactFlowInit,
    handleReactFlowMove,
    handleToggleMinimap,
    handleZoomIn,
    handleZoomOut,
    handleZoomSliderChange,
    isMinimapVisible,
    reactFlowInstance,
    setZoomLevel,
    zoomLevel,
  };
}
